import { useSelector } from 'react-redux';
import AsyncSelect from 'lib/AsyncSelect';
import { SelectHandler } from 'lib/AsyncSelect/AsyncSelect.types';
import { Asset } from 'components/common/types/Asset.types';
import { getNumberOfSharedCampaignPlanAssets } from 'store/sharedCampaignPlan/selectors';
import { CombinedSearchResult } from 'components/pages/Planner/PlannerMap/SearchBar/SearchBar.types';
import { CustomControl } from 'components/pages/Planner/PlannerMap/SearchBar/CustomComponents/CustomControl';
import { CustomMenuList } from 'components/pages/Planner/PlannerMap/SearchBar/CustomComponents/CustomMenuList';
import { CustomOption } from 'components/pages/Planner/PlannerMap/SearchBar/CustomComponents/CustomOption';
import { useCampaignPlanSearchData } from './useCampaignPlanSearchData';

interface CampaignPlanSearchBarProps {
  setBoundingBox: (boundingBox: string[]) => void;
  onAssetSelect: (coordinate: [number, number]) => void;
}

const createAssetBoundingBox = (asset: Asset): string[] => {
  const boundingBoxBuffer = 0.001;
  const minLat = (asset.latitude - boundingBoxBuffer).toString();
  const maxLat = (asset.latitude + boundingBoxBuffer).toString();
  const minLon = (asset.longitude - boundingBoxBuffer).toString();
  const maxLon = (asset.longitude + boundingBoxBuffer).toString();

  return [minLat, maxLat, minLon, maxLon];
};

const CampaignPlanSearchBar = ({ setBoundingBox, onAssetSelect }: CampaignPlanSearchBarProps): React.ReactElement => {
  const { loadCombinedResults } = useCampaignPlanSearchData();
  const totalAssets = useSelector(getNumberOfSharedCampaignPlanAssets);

  const handleSelect: SelectHandler<CombinedSearchResult> = (_, value: CombinedSearchResult | null) => {
    if (!value) return;

    if (value.type === 'location') {
      setBoundingBox(value.originalData.boundingbox);
    } else {
      setBoundingBox(createAssetBoundingBox(value.originalData));
      onAssetSelect([value.originalData.longitude, value.originalData.latitude]);
    }
  };

  return (
    <AsyncSelect
      dataTestId="campaign-plan-search-bar"
      name="campaign-plan-search-bar"
      placeholder="Search for frames or locations"
      textKey="displayText"
      valueKey="value"
      isDisabled={totalAssets === 0}
      loadData={loadCombinedResults}
      onSelect={handleSelect}
      customComponents={{ Control: CustomControl, Option: CustomOption, MenuList: CustomMenuList }}
    />
  );
};

export default CampaignPlanSearchBar;
